import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * Session Timeout Hook
 * 
 * Automatically logs out user after 30 minutes of inactivity
 * Shows warning 2 minutes before logout
 */
export function useSessionTimeout() {
  const { user, logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const intervalRef = useRef(null);

  // Session timeout settings (in milliseconds)
  const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
  const WARNING_TIME = 2 * 60 * 1000;     // 2 minutes before timeout

  const resetTimeout = () => {
    // Clear existing timeouts
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);
    
    // Hide warning
    setShowWarning(false);

    // Only set timeout if user is logged in
    if (!user) return;

    // Set warning timeout (28 minutes)
    warningTimeoutRef.current = setTimeout(() => {
      setShowWarning(true);
      setTimeLeft(WARNING_TIME / 1000); // seconds

      // Start countdown
      intervalRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(intervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, SESSION_TIMEOUT - WARNING_TIME);

    // Set logout timeout (30 minutes)
    timeoutRef.current = setTimeout(() => {
      console.log('Session expired - logging out');
      logout();
      setShowWarning(false);
      alert('Your session has expired. Please log in again.');
      window.location.href = '/login';
    }, SESSION_TIMEOUT);
  };

  const extendSession = () => {
    console.log('Session extended');
    resetTimeout();
  };

  useEffect(() => {
    if (!user) {
      // Clear timeouts when user logs out
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
      setShowWarning(false);
      return;
    }

    // Start timeout on mount and user login
    resetTimeout();

    // Events that reset the timeout (user activity)
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Debounce the reset to avoid too many calls
    let debounceTimer;
    const debouncedReset = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(resetTimeout, 1000); // Reset after 1 second of inactivity
    };

    // Add event listeners
    events.forEach((event) => {
      window.addEventListener(event, debouncedReset);
    });

    // Cleanup
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, debouncedReset);
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
      clearTimeout(debounceTimer);
    };
  }, [user]);

  return { showWarning, timeLeft, extendSession };
}

/**
 * Session Timeout Warning Modal Component
 */
export function SessionTimeoutWarning({ show, timeLeft, onExtend }) {
  if (!show) return null;

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          padding: '30px',
          borderRadius: '8px',
          maxWidth: '400px',
          textAlign: 'center',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
        }}
      >
        <h2 style={{ marginTop: 0, color: '#d32f2f' }}>Session Expiring Soon</h2>
        <p style={{ fontSize: '16px', marginBottom: '20px' }}>
          Your session will expire in:
        </p>
        <div
          style={{
            fontSize: '48px',
            fontWeight: 'bold',
            color: '#d32f2f',
            marginBottom: '20px'
          }}
        >
          {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
        </div>
        <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
          Click below to continue your session
        </p>
        <button
          onClick={onExtend}
          style={{
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            padding: '12px 30px',
            fontSize: '16px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
          onMouseOver={(e) => (e.target.style.backgroundColor = '#0056b3')}
          onMouseOut={(e) => (e.target.style.backgroundColor = '#007bff')}
        >
          Continue Session
        </button>
      </div>
    </div>
  );
}
